import React, {useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {Badge, Box, Button, Flex, Text, Textarea, useToast} from '@chakra-ui/react';
import {resolveReviewItem} from '../../utils/apis/agentApi';

export const issueQuery = (issue: any) => issue.id ? `flag=${issue.id}` : `case=${issue.case_id}`;
export const conversationLink = (issue: any) => `/conversations?phone=${encodeURIComponent(issue.phone_number)}&${issueQuery(issue)}`;
export const issueNote = (context: any): string => typeof context === 'string' ? context
  : context?.note || context?.summary || context?.action_needed || context?.issue || 'No note supplied.';

/** Shared issue details and resolution action, on the queue and conversation. */
export default function ReviewIssue({issue, onResolved, inConversation = false}: {
  issue: any; onResolved: (issue: any) => void; inConversation?: boolean;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const pending = useRef(false);
  const toast = useToast();
  const resolve = async () => {
    if (pending.current) return;
    pending.current = true; setBusy(true);
    try {
      const result = await resolveReviewItem(issue, note.trim() || undefined);
      if (!result.ok) throw new Error('Resolution failed');
      onResolved({...issue, resolved: true, resolution_note: note.trim(), resolved_at: new Date().toISOString()});
      toast({title: 'Issue resolved', status: 'success', duration: 2500});
    } catch {
      toast({title: 'Could not resolve this issue', description: 'It remains open. Please try again.', status: 'error'});
    } finally {pending.current = false; setBusy(false);}
  };
  return <Box borderWidth="1px" borderRadius="md" p={3} minW={0} bg="admin.subtle" aria-label={`Review issue ${issue.id || issue.case_id}`}>
    <Flex gap={2} wrap="wrap" align="center" mb={2}>
      <Text fontWeight="bold">{issue.id ? 'Issue' : 'Case'} #{issue.id || issue.case_id}</Text>
      <Badge colorScheme="orange">{String(issue.reason || 'other').replace(/_/g, ' ')}</Badge>
      <Badge colorScheme={issue.resolved ? 'green' : 'orange'}>{issue.resolved ? 'Resolved' : 'Open'}</Badge>
    </Flex>
    <Text whiteSpace="pre-wrap" overflowWrap="anywhere">{issueNote(issue.context)}</Text>
    {issue.context && typeof issue.context === 'object' && <Box as="details" mt={2} fontSize="sm">
      <summary>Full issue context</summary>
      <Text as="pre" whiteSpace="pre-wrap" overflowWrap="anywhere">{JSON.stringify(issue.context, null, 2)}</Text>
    </Box>}
    {issue.resolution_note && <Text mt={2} fontSize="sm">Resolution: {issue.resolution_note}</Text>}
    {!issue.resolved && <Textarea mt={3} size="sm" rows={2} value={note} onChange={e => setNote(e.target.value)}
      aria-label={`Resolution note for ${issue.id || issue.case_id}`} placeholder="Resolution note (optional)" isDisabled={busy} />}
    <Flex gap={2} wrap="wrap" mt={3}>
      {inConversation ? <Button as={Link} to={`/flag-review?${issueQuery(issue)}`} size="sm" variant="outline">View in Review Queue</Button>
        : issue.phone_number && <Button as={Link} to={conversationLink(issue)} size="sm" variant="outline">Open conversation</Button>}
      {!issue.resolved && <Button size="sm" colorScheme="purple" isLoading={busy} onClick={resolve}>Resolve issue</Button>}
    </Flex>
  </Box>;
}
